import { Fragment } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { BofuTabDocument, DocumentResponse } from "@interfaces/response";
import { MultiUploadCard } from "./MultiUploadCard";
import { UploadCard } from "./UploadCard";

/** Qué hacer con la lista de un documento múltiple. */
export type DocumentValueAction = "add" | "remove" | "update";

interface Props {
  documents: BofuTabDocument[];
  /** Documento simple: llega el valor nuevo, o `undefined` si se eliminó. */
  onChange: (params: { document: BofuTabDocument; newValue: DocumentResponse | undefined }) => void;
  /** Documento múltiple: además de la acción, el índice del valor que cambió. */
  onChangeMultiple: (params: {
    document: BofuTabDocument;
    newValue: DocumentResponse | undefined;
    valueAction: DocumentValueAction;
    valueIndex?: number;
  }) => void;
}

/** Props que salen del documento y son iguales en el caso simple y en el múltiple. */
const cardProps = (docItem: BofuTabDocument) => ({
  title: docItem.name,
  sub: docItem.label || undefined,
  required: docItem.required,
  disabled: docItem.disabled,
  fileCode: docItem.file_category_code,
  uploadType: docItem.upload_type || ("client" as const),
  clientDetailsId: docItem.client_details_id,
  personId: docItem.person_id,
  guaranteeId: docItem.guarantee_id,
});

/**
 * Lista de documentos del expediente con el `UploadCard` del kit nuevo, equivalente a
 * `components/TabBofu/DocumentsTabBofu.tsx`: recibe los documentos del store del expediente
 * (`BofuTabDocument`) y avisa cada cambio para que el padre los guarde.
 *
 * Los documentos `multiple` muestran una card por valor más el enlace para agregar otro; el
 * enlace se bloquea mientras el último valor esté vacío, para no acumular cards sin archivo.
 *
 * Entre card y card va un `Divider`, también entre los valores de un documento múltiple: desde que
 * el `UploadCard` dejó de traer borde propio, la línea es lo único que separa un documento del
 * siguiente.
 */
export const DocumentList = ({ documents, onChange, onChangeMultiple }: Props) => {
  if (documents.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: "32px" }}>
        <Typography variant="body1" sx={{ color: "primary.dark" }}>
          No se encontraron documentos para esta sección
        </Typography>
        <Typography variant="body2" sx={{ color: "text.disabled", mt: "4px" }}>
          Por favor contacta a tu asesor para obtener más información
        </Typography>
      </Box>
    );
  }

  return (
    // Sin `gap`: el separador va pegado a las cards y el aire lo pone el padding de cada una.
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {documents.map((docItem, docIndex) => (
        <Fragment key={docItem.name}>
          {docIndex > 0 && <Divider />}

          {docItem.multiple ? (
            <MultiUploadCard
              {...cardProps(docItem)}
              documents={docItem.list_values}
              maxFiles={docItem.max_files}
              onDocumentChange={({ newValue, valueAction, valueIndex }) =>
                onChangeMultiple({ document: docItem, newValue, valueAction, valueIndex })
              }
            />
          ) : (
            <UploadCard
              {...cardProps(docItem)}
              document={docItem.file_value}
              onDocumentChange={(newValue) => onChange({ document: docItem, newValue })}
            />
          )}
        </Fragment>
      ))}
    </Box>
  );
};
